import { Grid, LinearProgress, Stack, Typography } from "@mui/material";
import omit from "lodash/omit";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { UserRole } from "../auth/auth";
import dayjs from "../dayjs";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import { useCreateQuote } from "../quotes/useCreateQuote";
import ExpandableTasks from "../tasks/ExpandableTasks";
import EditOrderAdminActions from "./EditOrderAdminActions";
import ExpandableEmailsSentForOrder from "./emailsSent/ExpandableEmailsSentForOrder";
import ExpandableOrderNotes from "./notes/ExpandableOrderNotes";
import OrderEdit from "./orderEdit/OrderEdit";
import type { SavedOrder } from "./orderTypes";
import ExpandableOrderTodos from "./todos/ExpandableOrderTodos";
import { shouldShowTodos } from "./todos/shouldShowTodos";
import { useOrder } from "./useOrder";

const EditOrderPage: FC<{
  role: Exclude<UserRole, "CUSTOMER">;
}> = ({ role }) => {
  const navigate = useNavigate();
  const { openInNewTab } = useOpenInNewTab();
  const { createQuote } = useCreateQuote();
  const {
    order: existingOrder,
    isLoading,
    onUpdate,
    onUpdatePayments,
    onDelete,
    onCancelProduct,
    onRestoreProduct,
    onMoveProduct,
  } = useOrder();
  const [order, setOrder] = useState<SavedOrder | undefined>(existingOrder);

  useEffect(() => {
    setOrder(existingOrder);
  }, [existingOrder]);

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!order) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Takie zamówienie nie istnieje</Typography>
      </Grid>
    );
  }

  const navigateToOrderList = () => navigate("/orders");

  const handleSubmit = async () => {
    const { customerId, customerContactPersonEmail, products, ...update } = omit(
      order,
      "id",
      "created",
      "files",
      "userFriendlyId",
      "todos",
    );
    const orderToSubmit = {
      ...update,
      customer: {
        id: customerId,
        contactPersonEmail: customerContactPersonEmail,
      },
      products: products.map((p) => ({
        id: p.id,
        date: p.date,
        time: p.time ? p.time : undefined,
        bookingNumber: p.bookingNumber ? p.bookingNumber : undefined,
        note: p.note ? p.note : undefined,
        options: p.options.map((o) => ({ id: o.id, quantity: o.quantity, price: o.price ?? undefined })),
      })),
    };
    await onUpdate(orderToSubmit);
  };

  const handleDelete = async () => {
    await onDelete();
    navigateToOrderList();
  };

  const handleDuplicate = () => {
    openInNewTab(`/orders/new?from=${order.id}`);
  };

  const handleGoToParticipantList = () => {
    navigate(`/orders/${order.id}/participant-lists`);
  };

  const handleCreateCalculation = () => {
    navigate(`/calculations/new?fromOrder=${order.id}`);
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "baseline",
          }}
        >
          <Typography variant="h6">Zamówienie {order.userFriendlyId}</Typography>
          {order.created && <Typography variant="body2">{dayjs(order.created).format("DD.MM.YYYY")}</Typography>}
        </Stack>
        <Typography variant="body2">
          <Link to={`/costs?query=${order.userFriendlyId}`}>Zobacz powiązane koszty</Link>
        </Typography>
      </Grid>
      <OrderEdit
        order={order}
        role={role}
        onChange={(o) => setOrder((prev) => (prev ? { ...prev, ...(o as Partial<SavedOrder>) } : prev))}
        onUpdatePayments={async (p) => {
          await onUpdatePayments(p);
        }}
        onCancelProduct={async (productIndexes, bookingNumber, date) => {
          await onCancelProduct(productIndexes, bookingNumber, date);
        }}
        onRestoreProduct={
          role === "ADMIN"
            ? async (productIndexes, bookingNumber) => {
                await onRestoreProduct(productIndexes, bookingNumber);
              }
            : undefined
        }
        onMoveProduct={async (productIndexes, targetOrderId) => {
          await onMoveProduct(productIndexes, targetOrderId);
        }}
      />
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <EditOrderAdminActions
          order={order}
          onDuplicate={handleDuplicate}
          onGoToParticipantList={handleGoToParticipantList}
          onCreateCalculation={handleCreateCalculation}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onRequestQuote={async () => {
            await createQuote({ prepareFor: { type: "ORDER", id: order.id } });
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 8 }}>
        <ExpandableTasks entityType="orders" entityId={order.id} customerId={order.customerId} />
        <ExpandableOrderNotes orderId={order.id} contactDate={order.contactDate} />
      </Grid>
      <Grid size={{ xs: 12, lg: 4 }}>
        {shouldShowTodos(order) && <ExpandableOrderTodos orderId={order.id} todos={order.todos} />}
        <ExpandableEmailsSentForOrder orderId={order.id} />
      </Grid>
    </Grid>
  );
};

export default EditOrderPage;
